import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Home, Building2, HardHat, Package, KeyRound, Sparkles, Plus, Phone, MapPin, ShieldCheck, AlertTriangle, Store } from "lucide-react";
import { toast } from "sonner";
import { getApp, APPS } from "../data/apps";
import { Reveal } from "../components/Shared";
import { AppIcon } from "../components/AppIcon";
import { ContactCTA } from "../components/ContactCTA";
import { LeadModal, Stars } from "../components/ConstructionForms";
import { submitQuote, orderMaterial, reportIssue, registerArtisan, requestArtisan, rateArtisan, fetchArtisans, fetchPublicProperties, photoUrl } from "../lib/api";

const SECTION_ICONS = { particuliers: Home, entreprises: Building2, pros: HardHat, materiaux: Package, gestion: KeyRound };
const TRADES = ["Maçon", "Électricien", "Plombier", "Carreleur", "Peintre", "Menuisier", "Soudeur"];
const ACCENT = "#B45309";

export default function Construction() {
  const app = getApp("construction");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const others = APPS.filter((a) => a.slug !== app.slug);

  const [modal, setModal] = useState(null);
  const [artisans, setArtisans] = useState([]);
  const [filter, setFilter] = useState("");
  const [properties, setProperties] = useState([]);

  const loadArtisans = () => fetchArtisans().then(setArtisans).catch(() => {});
  useEffect(() => { loadArtisans(); fetchPublicProperties().then(setProperties).catch(() => {}); }, []);

  const contactFields = [
    { name: "name", label: "Votre nom", required: true },
    { name: "phone", label: "Téléphone", type: "tel", required: true },
  ];

  const openQuote = (service, kind) => setModal({
    title: "Demander un devis",
    subtitle: service,
    submitLabel: "Envoyer ma demande",
    fields: [
      ...contactFields,
      { name: "email", label: "Email", type: "email" },
      { name: "city", label: "Ville" },
      { name: "details", label: "Décrivez votre projet", type: "textarea", placeholder: "Surface, budget, délais…" },
    ],
    onSubmit: (f) => submitQuote({ kind, service, ...f }),
  });

  const openMaterial = (material) => setModal({
    title: "Commander un matériau",
    subtitle: material,
    submitLabel: "Demander un prix",
    fields: [
      { name: "quantity", label: "Quantité souhaitée", placeholder: "ex : 50 sacs, 2 tonnes…" },
      ...contactFields,
      { name: "city", label: "Ville de livraison" },
    ],
    onSubmit: (f) => orderMaterial({ material, ...f }),
  });

  const openIssue = () => setModal({
    title: "Signaler une panne",
    subtitle: "Nous intervenons au plus vite.",
    submitLabel: "Envoyer le signalement",
    fields: [
      { name: "property_ref", label: "Bien concerné / adresse", required: true },
      ...contactFields,
      { name: "issue", label: "Décrivez la panne", type: "textarea", required: true, placeholder: "Fuite, électricité, serrurerie…" },
    ],
    onSubmit: (f) => reportIssue(f),
  });

  const openRegister = () => setModal({
    title: "Devenir artisan partenaire",
    subtitle: "Créez votre profil, recevez des demandes de clients.",
    submitLabel: "Créer mon profil",
    success: "Profil envoyé ! Il sera visible après validation par notre équipe.",
    fields: [
      { name: "name", label: "Nom / entreprise", required: true },
      { name: "trade", label: "Métier", type: "select", options: TRADES, required: true },
      { name: "zone", label: "Zone d'intervention", required: true, placeholder: "ex : Bamako, Kati…" },
      { name: "phone", label: "Téléphone", type: "tel", required: true },
      { name: "email", label: "Email" },
      { name: "bio", label: "Présentation", type: "textarea", placeholder: "Expérience, spécialités…" },
    ],
    onSubmit: (f) => registerArtisan(f),
  });

  const openArtisanContact = (a) => setModal({
    title: `Contacter ${a.name}`,
    subtitle: `${a.trade} · ${a.zone}`,
    submitLabel: "Envoyer ma demande",
    fields: [...contactFields, { name: "message", label: "Votre besoin", type: "textarea", placeholder: "Décrivez vos travaux…" }],
    onSubmit: (f) => requestArtisan(a.id, f),
  });

  const rate = async (a, n) => {
    try {
      await rateArtisan(a.id, { rating: n });
      toast.success("Merci pour votre note !");
      loadArtisans();
    } catch {
      toast.error("Impossible d'enregistrer la note");
    }
  };

  const shown = filter ? artisans.filter((a) => a.trade === filter) : artisans;

  return (
    <main data-testid="app-detail-construction">
      {/* Hero */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden">
        <motion.img src={app.heroImage} style={{ y: bgY }} alt={`${app.name} — ${app.category}`} className="absolute inset-0 h-[118%] w-full object-cover blur-[2px]" />
        <div className="absolute inset-0 bg-navy/72" />
        <div className="absolute inset-0" style={{ background: `radial-gradient(120% 90% at 15% 20%, ${ACCENT}33 0%, transparent 55%)` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/30" />

        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pt-28 pb-16 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Link to="/applications" data-testid="back-to-apps" className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors">
              <ArrowLeft size={16} /> Toutes nos activités
            </Link>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="mt-8">
              <AppIcon app={app} variant="solid" iconSize={32} containerClass="h-16 w-16 rounded-2xl shadow-xl" />
            </motion.div>
            <h1 className="mt-6 font-display font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl drop-shadow-sm">{app.name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-sm font-medium text-white">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ACCENT }} /> {app.category}
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-sm font-medium text-white/90">
                <Sparkles size={14} style={{ color: ACCENT }} /> Services disponibles
              </div>
            </div>
            <p className="mt-5 max-w-lg text-white/80 leading-relaxed text-lg">{app.tagline}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={() => openQuote("Construction / rénovation", "particulier")} data-testid="construction-cta-devis" className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy hover:bg-white/90 transition-colors">
                Demander un devis <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#rubriques" data-testid="construction-cta-rubriques" className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/5 backdrop-blur-md px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/15 transition-colors">
                Découvrir les services
              </a>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md overflow-hidden rounded-[1.75rem] border border-white/20 shadow-2xl">
              <img src={app.image} alt={`${app.name} — chantier`} className="w-full h-auto block" />
              <div className="pointer-events-none absolute inset-0 rounded-[1.75rem] ring-1 ring-inset ring-white/40" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Complémentarité */}
      <section className="bg-navy py-14 text-white">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <p className="text-lg sm:text-xl leading-relaxed text-white/85">{app.intro}</p>
          <Link to="/applications/maison" data-testid="construction-link-maison" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors">
            Découvrir Une Maison Pour Tous <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Rubriques */}
      <section id="rubriques" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nos services</div>
              <h2 className="mt-4 font-display font-semibold tracking-tight text-navy text-3xl sm:text-4xl">Un écosystème complet de la construction et de l'habitat</h2>
            </div>
          </Reveal>

          {/* Devis (particuliers + entreprises) */}
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {app.sections.filter((s) => s.icon === "particuliers" || s.icon === "entreprises").map((s, i) => {
              const Icon = SECTION_ICONS[s.icon];
              const kind = s.icon === "entreprises" ? "entreprise" : "particulier";
              return (
                <Reveal key={s.title} i={i}>
                  <div data-testid={`construction-section-${s.icon}`} className="flex h-full flex-col rounded-3xl border border-border p-7 transition-all hover:shadow-lg" style={{ backgroundColor: app.tint }}>
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl" style={{ backgroundColor: ACCENT, color: "#fff" }}><Icon size={24} /></span>
                      <div><h3 className="font-display font-semibold text-navy text-xl">{s.title}</h3><p className="text-sm text-muted-foreground">{s.desc}</p></div>
                    </div>
                    <div className="mt-6 grid gap-2.5">
                      {s.items.map((it) => (
                        <button key={it} type="button" data-testid={`quote-btn-${it}`} onClick={() => openQuote(it, kind)} className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-white px-4 py-3 text-left text-sm font-medium text-navy transition-all hover:-translate-y-0.5 hover:shadow-sm">
                          {it}
                          <ArrowRight size={15} className="text-muted-foreground transition-transform group-hover:translate-x-0.5" style={{ color: ACCENT }} />
                        </button>
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Professionnels — annuaire artisans */}
          <div className="mt-16 rounded-3xl border border-border p-7 sm:p-10" style={{ backgroundColor: app.tint }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl" style={{ backgroundColor: ACCENT, color: "#fff" }}><HardHat size={24} /></span>
                <div>
                  <h3 className="font-display font-semibold text-navy text-2xl">Trouver des professionnels</h3>
                  <p className="text-sm text-muted-foreground">Des artisans vérifiés, notés par les clients.</p>
                </div>
              </div>
              <button onClick={openRegister} data-testid="artisan-register-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: ACCENT }}>
                <Plus size={16} /> Devenir artisan
              </button>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={() => setFilter("")} className="rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors" style={filter === "" ? { backgroundColor: ACCENT, color: "#fff" } : { backgroundColor: "#fff", color: "#5b6472" }}>Tous</button>
              {TRADES.map((t) => (
                <button key={t} data-testid={`trade-filter-${t}`} onClick={() => setFilter(t)} className="rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors" style={filter === t ? { backgroundColor: ACCENT, color: "#fff" } : { backgroundColor: "#fff", color: "#5b6472" }}>{t}</button>
              ))}
            </div>

            {shown.length === 0 ? (
              <div className="mt-8 rounded-2xl border border-dashed border-border bg-white/60 p-8 text-center" data-testid="artisans-empty">
                <p className="text-navy font-medium">Aucun artisan {filter && `« ${filter} »`} pour le moment.</p>
                <p className="mt-1 text-sm text-muted-foreground">Soyez le premier à rejoindre notre réseau d'artisans partenaires.</p>
                <button onClick={openRegister} className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: ACCENT }}><Plus size={16} /> Créer mon profil</button>
              </div>
            ) : (
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((a) => (
                  <div key={a.id} data-testid="artisan-card" className="flex flex-col rounded-2xl border border-border bg-white p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: ACCENT }}>{a.name.charAt(0).toUpperCase()}</span>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-navy truncate">{a.name}</div>
                        <div className="text-xs text-muted-foreground">{a.trade}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin size={13} /> {a.zone}</div>
                    {a.bio && <p className="mt-2 text-sm text-navy/70 line-clamp-2">{a.bio}</p>}
                    <div className="mt-3 flex items-center gap-2">
                      <Stars value={Math.round(a.rating)} readOnly accent={ACCENT} idPrefix={`star-readonly-${a.id}`} />
                      <span className="text-xs text-muted-foreground">{a.rating_count > 0 ? `${a.rating} (${a.rating_count})` : "Nouveau"}</span>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <button onClick={() => openArtisanContact(a)} data-testid={`artisan-contact-${a.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: ACCENT }}><Phone size={14} /> Contacter</button>
                    </div>
                    <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
                      <span className="text-xs text-muted-foreground">Noter :</span>
                      <Stars value={0} accent={ACCENT} onRate={(n) => rate(a, n)} idPrefix={`star-rate-${a.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Matériaux */}
          {app.sections.filter((s) => s.icon === "materiaux").map((s) => (
            <div key={s.title} data-testid="construction-section-materiaux" className="mt-16 rounded-3xl border border-border p-7 sm:p-10" style={{ backgroundColor: app.tint }}>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl" style={{ backgroundColor: ACCENT, color: "#fff" }}><Package size={24} /></span>
                <div><h3 className="font-display font-semibold text-navy text-2xl">{s.title}</h3><p className="text-sm text-muted-foreground">{s.desc}</p></div>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {s.items.map((it) => (
                  <button key={it} type="button" data-testid={`material-btn-${it}`} onClick={() => openMaterial(it)} className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-white px-4 py-3 text-left text-sm font-medium text-navy transition-all hover:-translate-y-0.5 hover:shadow-sm">
                    {it}<Plus size={15} style={{ color: ACCENT }} />
                  </button>
                ))}
              </div>
              {s.note && <p className="mt-5 rounded-xl bg-white/70 px-4 py-3 text-sm text-navy/70">{s.note}</p>}
            </div>
          ))}

          {/* Gestion immobilière */}
          {app.sections.filter((s) => s.icon === "gestion").map((s) => (
            <div key={s.title} data-testid="construction-section-gestion" className="mt-16 rounded-3xl border border-border p-7 sm:p-10" style={{ backgroundColor: app.tint }}>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl" style={{ backgroundColor: ACCENT, color: "#fff" }}><KeyRound size={24} /></span>
                <div><h3 className="font-display font-semibold text-navy text-2xl">{s.title}</h3><p className="text-sm text-muted-foreground">{s.desc}</p></div>
              </div>
              <ul className="mt-6 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 rounded-xl bg-white px-4 py-3 text-sm text-navy/85"><Check size={16} className="mt-0.5 shrink-0" style={{ color: ACCENT }} />{it}</li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl bg-white/70 p-5">
                <ShieldCheck size={22} style={{ color: ACCENT }} />
                <p className="flex-1 text-sm text-navy/75">Espace réservé au propriétaire — vos biens, locataires, loyers et contrats sont gérés depuis l'administration. Les locataires peuvent signaler une panne ci-dessous.</p>
                <button onClick={openIssue} data-testid="report-issue-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: ACCENT }}>
                  <AlertTriangle size={16} /> Signaler une panne
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Biens à louer (catalogue public) */}
      {properties.length > 0 && (
        <section id="biens" className="border-t border-border bg-white py-20 lg:py-24" data-testid="public-properties">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <div className="max-w-2xl">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Location</div>
                <h2 className="mt-4 font-display font-semibold tracking-tight text-navy text-3xl sm:text-4xl">Biens disponibles à la location</h2>
                <p className="mt-3 text-muted-foreground">Appartements et magasins gérés par TIRERA GROUP. Contactez-nous pour une visite.</p>
              </div>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {properties.map((p, i) => (
                <Reveal key={p.id} i={i}>
                  <div data-testid="public-property-card" className="flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                      {p.photos && p.photos.length > 0 ? (
                        <img src={photoUrl(p.photos[0])} alt={p.label} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                          {p.type === "magasin" ? <Store size={40} /> : <Home size={40} />}
                        </div>
                      )}
                      <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 text-xs font-medium capitalize text-white backdrop-blur-sm">
                        {p.type === "magasin" ? "Magasin" : "Appartement"}
                      </span>
                      {p.photos && p.photos.length > 1 && (
                        <span className="absolute right-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium text-white">{p.photos.length} photos</span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-display font-semibold text-navy text-lg">{p.label}</h3>
                      {p.address && <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin size={14} /> {p.address}</div>}
                      {p.description && <p className="mt-2 text-sm text-navy/70 line-clamp-3">{p.description}</p>}
                      <div className="mt-auto pt-4">
                        {p.rent ? <div className="font-display text-xl font-bold" style={{ color: ACCENT }}>{p.rent.toLocaleString("fr-FR")} <span className="text-sm font-medium text-muted-foreground">FCFA/mois</span></div> : null}
                        <button onClick={() => openQuote(`Visite — ${p.label}`, "particulier")} data-testid={`property-visit-${p.id}`} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: ACCENT }}>
                          Demander une visite <ArrowRight size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other activities */}
      <section className="border-t border-border py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h3 className="font-display font-semibold text-navy text-xl mb-6">Découvrez aussi</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((a) => (
              <Link key={a.slug} to={`/applications/${a.slug}`} data-testid={`related-app-${a.slug}`} className="group flex items-center gap-4 rounded-2xl border border-border p-5 hover:-translate-y-1 hover:shadow-lg transition-all">
                <AppIcon app={a} variant="solid" iconSize={20} containerClass="h-11 w-11 rounded-xl" />
                <div><div className="font-display font-semibold text-navy">{a.name}</div><div className="text-xs text-muted-foreground">{a.category}</div></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />

      <LeadModal open={!!modal} config={modal} accent={ACCENT} onClose={() => setModal(null)} />
    </main>
  );
}
